import React, { useEffect } from 'react';
import { X, Building2, Search, Briefcase, CheckCircle } from 'lucide-react';
import { useHashRouter } from '../hooks/useHashRouter';

interface Props {
  onClose: () => void;
}

export const RolePickerModal: React.FC<Props> = ({ onClose }) => {
  const { navigate } = useHashRouter();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const pick = (role: 'owner' | 'seeker' | 'vendor') => {
    sessionStorage.setItem('signup_role', role);
    navigate('/signup');
    onClose();
  };

  const goLogin = () => {
    navigate('/login');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative bg-white dark:bg-stone-900 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[calc(100vh-2rem)] overflow-y-auto animate-fadeIn">

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all z-10"
        >
          <X size={20} />
        </button>

        <div className="p-8 pb-6">
          <div className="text-center mb-6 mt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-accent-terra mb-2">Vitajte na <span translate="no" className="notranslate">Espacero</span></p>
            <h2 className="text-2xl font-black text-stone-800 dark:text-stone-100">Ako chcete používať platformu?</h2>
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Vyberte svoju rolu a my vás prevedieme zvyškom</p>
          </div>

          {/* Role cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">

            {/* Owner */}
            <button
              onClick={() => pick('owner')}
              className="group text-left p-6 rounded-2xl border-2 border-stone-200 dark:border-stone-700 hover:border-accent-terra dark:hover:border-accent-terra hover:shadow-lg hover:shadow-accent-terra/10 transition-all duration-200 bg-stone-50 dark:bg-stone-800/50 hover:bg-orange-50/50 dark:hover:bg-stone-800"
            >
              <div className="w-12 h-12 rounded-2xl bg-accent-terra/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Building2 size={24} className="text-accent-terra" />
              </div>
              <h3 className="font-extrabold text-stone-900 dark:text-white text-lg mb-1">Mám priestor</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 leading-relaxed">
                Zverejním inzerát a čakám na záujemcov
              </p>
              <ul className="space-y-1.5">
                {['Bezplatné zverejnenie', 'Správa rezervácií', 'Priame oslovenie klientov'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <CheckCircle size={13} className="text-accent-terra flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-center gap-1 text-sm font-bold text-cta group-hover:gap-2 transition-all">
                Začať ako majiteľ <span>→</span>
              </div>
            </button>

            {/* Seeker */}
            <button
              onClick={() => pick('seeker')}
              className="group text-left p-6 rounded-2xl border-2 border-stone-200 dark:border-stone-700 hover:border-accent-terra dark:hover:border-accent-terra hover:shadow-lg hover:shadow-accent-terra/10 transition-all duration-200 bg-stone-50 dark:bg-stone-800/50 hover:bg-accent-terra/10/50 dark:hover:bg-stone-800"
            >
              <div className="w-12 h-12 rounded-2xl bg-accent-terra/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Search size={24} className="text-accent-terra" />
              </div>
              <h3 className="font-extrabold text-stone-900 dark:text-white text-lg mb-1">Hľadám priestor</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 leading-relaxed">
                Zverejním dopyt a majitelia mi napíšu sami
              </p>
              <ul className="space-y-1.5">
                {['Dopyt na mieru', 'Majitelia vás oslovia', 'Porovnáte viac ponúk'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <CheckCircle size={13} className="text-accent-terra flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-center gap-1 text-sm font-bold text-cta group-hover:gap-2 transition-all">
                Hľadať ako klient <span>→</span>
              </div>
            </button>

            {/* Vendor */}
            <button
              onClick={() => pick('vendor')}
              className="group text-left p-6 rounded-2xl border-2 border-stone-200 dark:border-stone-700 hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-200 bg-stone-50 dark:bg-stone-800/50 hover:bg-emerald-50/50 dark:hover:bg-stone-800"
            >
              <div className="w-12 h-12 rounded-2xl bg-accent-terra/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Briefcase size={24} className="text-accent-terra" />
              </div>
              <h3 className="font-extrabold text-stone-900 dark:text-white text-lg mb-1">Ponúkam služby</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 leading-relaxed">
                Catering, foto, DJ — doplnkové služby k eventom
              </p>
              <ul className="space-y-1.5">
                {['Vlastný profil služby', 'Zviditeľnenie na trhu', 'Priame oslovenie'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <CheckCircle size={13} className="text-accent-terra flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-center gap-1 text-sm font-bold text-cta group-hover:gap-2 transition-all">
                Začať ako vendor <span>→</span>
              </div>
            </button>
          </div>

          {/* Login link */}
          <p className="text-center text-sm text-stone-500 dark:text-stone-400">
            Už máte účet?{' '}
            <button onClick={goLogin} className="font-bold text-stone-600 dark:text-stone-300 hover:text-accent-terra transition-colors">
              Prihlásiť sa
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
